<template>
 <modal :show="modalVisible" size="lg" @close="closeModal">
  <div class="d-flex justify-content-between align-items-center ml-2 mr-2">
    <h2 class="mb-0" v-once>Device Provisioning Status</h2>
    <button
      type="button"
      class="close"
      aria-label="Close"
      @click="closeModal"
      style="font-size:1.5rem;line-height:1;background:none;border:none;cursor:pointer;color:#6c757d;padding:0 4px;"
    >
      <span aria-hidden="true">&times;</span>
    </button>
  </div>
    <div class="p-2">

      <!-- Device ID / Session ID card -->
      <div class="card bg-secondary border shadow-none mb-2" v-once>
        <div class="card-body py-2 px-3">
          <div class="d-flex flex-wrap" style="gap: 8px;">
            <div v-if="provisioningData.deviceId" class="d-flex align-items-center mr-3">
              <i class="fas fa-microchip text-info mr-2"></i>
              <div>
                <small class="text-muted font-weight-bold d-block" style="font-size:0.65rem;">Device ID</small>
                <small class="text-dark font-weight-bold" style="word-break:break-all;">{{ provisioningData.deviceId }}</small>
              </div>
            </div>
            <div v-if="provisioningData.session_id" class="d-flex align-items-center">
              <i class="fas fa-plug text-info mr-2"></i>
              <div>
                <small class="text-muted font-weight-bold d-block" style="font-size:0.65rem;">Session ID</small>
                <small class="text-dark font-weight-bold" style="word-break:break-all;font-size:0.7rem;">{{ provisioningData.session_id }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Stats cards -->
      <b-row class="no-gutters mb-2">
        <b-col cols="6" md="3" class="pr-1 pb-1">
          <div class="card border shadow-none mb-0 h-100">
            <div class="card-body py-2 px-2 d-flex align-items-center">
              <i class="fas fa-file-alt text-gray-800 mr-2" style="font-size:1.2rem;flex-shrink:0;"></i>
              <div style="min-width:0;">
                <div class="stat-label text-gray-800">Total</div>
                <div class="stat-value text-gray-800">{{ provisioningData.total_files }}</div>
              </div>
            </div>
          </div>
        </b-col>
        <b-col cols="6" md="3" class="pl-1 pr-md-1 pb-1">
          <div class="card border shadow-none mb-0 h-100">
            <div class="card-body py-2 px-2 d-flex align-items-center">
              <i class="fas fa-check-circle text-success mr-2" style="font-size:1.2rem;flex-shrink:0;"></i>
              <div style="min-width:0;">
                <div class="stat-label text-success">Done</div>
                <div class="stat-value text-success">{{ provisioningData.completed_files }}</div>
              </div>
            </div>
          </div>
        </b-col>
        <b-col cols="6" md="3" class="pr-1 pl-md-1 pb-1">
          <div class="card border shadow-none mb-0 h-100">
            <div class="card-body py-2 px-2 d-flex align-items-center">
              <i class="fas fa-times-circle text-danger mr-2" style="font-size:1.2rem;flex-shrink:0;"></i>
              <div style="min-width:0;">
                <div class="stat-label text-danger">Failed</div>
                <div class="stat-value text-danger">{{ provisioningData.failed_files }}</div>
              </div>
            </div>
          </div>
        </b-col>
        <b-col cols="6" md="3" class="pl-1 pb-1">
          <div class="card border shadow-none mb-0 h-100">
            <div class="card-body py-2 px-2 d-flex align-items-center">
              <i class="fas fa-download text-info mr-2" style="font-size:1.2rem;flex-shrink:0;"></i>
              <div style="min-width:0;">
                <div class="stat-label text-info">Downloading</div>
                <div class="stat-value text-info">{{ provisioningData.downloading_files }}</div>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Status card -->
      <div class="card shadow mb-2" :class="statusBgClass">
        <div class="card-body py-2 px-3">
          <div class="d-flex flex-wrap align-items-center justify-content-between" style="gap:6px;">
            <div class="d-flex align-items-center">
              <i class="fas fa-info-circle mr-2"></i>
              <strong>Status:</strong>
              <span class="ml-2 font-weight-bold" :class="statusTextClass">{{ provisionStatusText }}</span>
            </div>
            <div class="d-flex flex-wrap align-items-center" style="gap:6px;">
              <span class="badge badge-light text-primary" v-if="isProcessing">
                <i class="fas fa-sync-alt fa-spin mr-1"></i>Processing
              </span>
              <button
                v-if="isProcessing"
                class="btn btn-sm text-danger border-danger"
                style="font-size:0.7rem;background-color:#fff;padding:2px 8px;"
                :disabled="cancelling"
                @click="cancelProvisioning"
              >
                <i class="fas fa-ban mr-1"></i>
                {{ cancelling ? 'Cancelling...' : 'Cancel' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Error message -->
      <div v-if="provisioningData.errorMessage" class="card border border-danger shadow-none mb-2">
        <div class="card-body py-2 px-3 text-danger">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          <strong>Error:</strong> {{ provisioningData.errorMessage }}
        </div>
      </div>

      <!-- File details + Log side by side on md, stacked on mobile -->
      <b-row>
        <b-col cols="12" md="6" class="mb-2">
          <div v-if="provisioningData.files && provisioningData.files.length > 0" class="card shadow-none">
            <div
              class="card-header border bg-secondary py-2 cursor-pointer"
              @click="toggleFiles"
            >
              <div class="d-flex justify-content-between align-items-center">
                <span class="font-weight-bold" style="font-size:0.85rem;">
                  <i class="fas fa-folder-open text-muted mr-2"></i>File Details
                </span>
                <div class="d-flex align-items-center">
                  <small class="text-muted mr-2">{{ provisioningData.files.length }} files</small>
                  <i class="fas fa-chevron-down" :class="{ 'fa-rotate-180': filesExpanded }"></i>
                </div>
              </div>
            </div>
            <b-collapse v-model="filesExpanded" :id="`filesCollapse-${componentId}`">
              <div class="card-body p-0 border-left border-right" style="max-height:200px;overflow-y:auto;">
                <div
                  v-for="file in provisioningData.files"
                  :key="file.filename"
                  class="px-2 py-2 border-bottom"
                  style="background: white;"
                >
                  <div class="d-flex justify-content-between align-items-center flex-wrap" style="gap:4px;">
                    <div class="d-flex align-items-center" style="min-width:0;flex:1;">
                      <i class="fas fa-file text-muted mr-2" style="flex-shrink:0;"></i>
                      <span class="font-monospace text-dark" style="font-size:0.72rem;word-break:break-all;">{{ file.filename }}</span>
                    </div>
                    <div class="d-flex align-items-center" style="gap:4px;flex-shrink:0;">
                      <span class="badge text-uppercase" :class="getStatusBadgeClass(file.status)" style="font-size:0.6rem;">
                        <i :class="getStatusIcon(file.status)" class="mr-1"></i>{{ file.status }}
                      </span>
                      <button
                        v-if="file.status === 'pending' || file.status === 'downloading'"
                        class="btn btn-sm btn-outline-danger py-0 px-1"
                        style="font-size:0.65rem;line-height:1.4;"
                        :disabled="skippingFile === file.filename"
                        @click="skipFile(file.filename)"
                        title="Skip this file"
                      >
                        <i class="fas fa-ban"></i>
                      </button>
                    </div>
                  </div>
                  <div v-if="file.status === 'downloading'" class="mt-1">
                    <div class="progress" style="height:3px;border-radius:2px;">
                      <div class="progress-bar progress-bar-striped progress-bar-animated bg-primary" style="width:100%;"></div>
                    </div>
                  </div>
                </div>
              </div>
            </b-collapse>
          </div>
        </b-col>

        <b-col cols="12" md="6" class="mb-2">
          <div class="card shadow-none">
            <div
              class="card-header border py-2 cursor-pointer"
              @click="toggleLog"
            >
              <div class="d-flex justify-content-between align-items-center text-dark">
                <span class="font-weight-bold" style="font-size:0.85rem;">
                  <i class="fas fa-terminal mr-2"></i>Provisioning Log
                  <small class="ml-1 text-muted">({{ provisioningData.message?.length || 0 }})</small>
                </span>
                <i class="fas fa-chevron-down" :class="{ 'fa-rotate-180': logExpanded }"></i>
              </div>
            </div>
            <b-collapse v-model="logExpanded" :id="`logCollapse-${componentId}`">
              <div class="card-body p-0 bg-light border-left border-right" style="max-height:200px;overflow-y:auto;">
                <div
                  v-for="(entry, index) in provisioningData.message"
                  :key="index"
                  class="px-3 py-1 border-bottom small font-monospace"
                  :class="{ 'bg-white': index % 2 === 0 }"
                  style="font-size:0.72rem;"
                >
                  {{ entry }}
                </div>
                <div v-if="!provisioningData.message?.length" class="px-3 py-3 text-center text-muted small">
                  <i class="fas fa-info-circle mr-1"></i>No log entries yet
                </div>
              </div>
            </b-collapse>
          </div>
        </b-col>
      </b-row>

    </div>

    <template #modal-footer>
      <BaseButton variant="link" @click="closeModal">
        <i class="fas fa-times mr-1"></i> Close
      </BaseButton>
    </template>
  </modal>
</template>

<script>
import Modal from "@/views/components/theme/Modal";
import BaseButton from "@/views/components/theme/BaseButton";

export default {
  name: "ModalProvision",
  components: { Modal, BaseButton },
  props: {
    deviceId: { type: String, default: null },
    entity:   { type: String, default: null },
  },
  data() {
    return {
      modalVisible: false,
      provisionDone: false,
      provisioningData: {
        deviceId: null,
        files: [],
        completed_files: 0,
        failed_files: 0,
        downloading_files: 0,
        total_files: 0,
        message: [],
        errorMessage: "",
        status: "pending",
        session_id: null,
      },
      logExpanded: false,
      filesExpanded: false,
      componentId: Math.random().toString(36).substr(2, 9),
      pollTimer: null,
      cancelling: false,
      skippingFile: null,
    };
  },
  computed: {
   provisionStatusText() {
  const status = this.provisioningData.status || "idle";
  const { completed_files: completed, failed_files: failed, total_files: total } = this.provisioningData;

  // Always check counters first when session is terminal
  const isTerminalStatus = ["completed", "failed", "provision_completed", 
                             "provision_failed", "cancelled", "timeout"].includes(status);
  if (isTerminalStatus && total > 0) {
    if (failed === 0 && completed === total) return "Success";
    if (failed > 0 && completed > 0) return "Partially Succeeded";
    if (failed > 0 && completed === 0) return "Failed";
  }

  if (status === "cancelled") return "Cancelled";
  if (status === "timeout" || status === "provision_failed") return "Failed";
  if (status === 'interrupted') return "Interrupted";

  if (["in_progress", "provision_started", "downloading"].includes(status)) {
    if (parseInt(total) === parseInt(completed) + parseInt(failed)) {
      const s = this.getFileNumberStatus(completed, failed, total);
      if (s) return s;
    }
    return "In Progress";
  }

  return status.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
},
    isProcessing() {
      return this.provisionStatusText === "In Progress";
    },
    statusBgClass() {
      const s = this.provisionStatusText.toLowerCase();
      if (s === "success") return "bg-success text-white";
      if (s === "partially succeeded") return "bg-warning text-dark";
      if (s === "failed") return "bg-danger text-white";
      if (s === "cancelled") return "bg-secondary text-white";

      if (s === "interrupted") return "bg-interrupted text-white";
  return "bg-primary text-white";
    },
    statusTextClass() {
      return this.provisionStatusText.toLowerCase() === "partially succeeded" ? "text-dark" : "text-white";
    },
  },
  mounted() {
    this.modalVisible = true;
    if (this.deviceId && this.entity) this.startPolling();
    this._onVisible = () => {
      if (document.visibilityState === 'visible') this.poll();
    };
    document.addEventListener('visibilitychange', this._onVisible);
  },
  beforeUnmount() {
    this.stopPolling();
    if (this._onVisible) document.removeEventListener('visibilitychange', this._onVisible);
  },
  methods: {
    getFileNumberStatus(completed, failed, total) {
      if (total === 0) return null;
      if (completed === total) return "Success";
      if (failed === total) return "Failed";
      if (completed > 0 && failed > 0) return "Partially Succeeded";
      return null;
    },
    toggleLog()   { this.logExpanded   = !this.logExpanded; },
    toggleFiles() { this.filesExpanded = !this.filesExpanded; },
    getStatusBadgeClass(status) {
      return { completed: "badge-success", failed: "badge-danger", downloading: "badge-primary", pending: "badge-warning", queued: "badge-secondary" }[status?.toLowerCase()] || "badge-secondary";
    },
    getStatusIcon(status) {
      return { completed: "fas fa-check", failed: "fas fa-times", downloading: "fas fa-download", pending: "fas fa-clock", queued: "fas fa-pause" }[status?.toLowerCase()] || "fas fa-question";
    },
    getFileStatusClass(status) {
  return status === 'failed' ? 'bg-danger-subtle' : '';
},
    startPolling() {
      this.stopPolling();
      this.poll();
      this.pollTimer = setInterval(() => this.poll(), 2500);
    },
    stopPolling() {
      if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
    },
async poll() {
  try {
    const resp = await this.$http.get(
      `/tenant/${this.entity}/provision/${this.deviceId}/status`
    );
    
    // Session finished — backend cleared current_provision
    if (resp.data.status === 'no_active_provision') {
      this.stopPolling();
      try {
        const last = await this.$http.get(
          `/tenant/${this.entity}/provision/${this.deviceId}/last`
        );
       // console.log('[poll] /last response:', last.data);
        this.applyState(last.data, true);
      } catch (e) {
       // console.log('[poll] /last failed:', e);
      }
      return;  // ← stop here, don't call applyState with no_active_provision
    }

    this.applyState(resp.data, false);

    if (['completed', 'failed', 'cancelled', 'interrupted'].includes(resp.data.status)) {
      this.stopPolling();
    }
  } catch (err) {
    if (err.response?.status === 404) {
      try {
        const last = await this.$http.get(
          `/tenant/${this.entity}/provision/${this.deviceId}/last`
        );
        this.applyState(last.data, true);
      } catch (e) { /* no history */ }
      this.stopPolling();
    }
  }
},

applyState(data, isHistory) {
  if (!data) return;
  if (data.status === 'no_active_provision') return;

  const terminal = ['completed', 'failed', 'cancelled','interrupted'];
  const isTerminal = isHistory || terminal.includes(data.status);
  const prev = this.provisioningData;
  
  let newMessages = [...(prev.message || [])];

  if (isHistory) {
    // Always rebuild log from scratch when loading history
    // (live messages may be stale/incomplete)
    newMessages = [];
    (data.files || []).forEach(f => {
      if (f.status === 'completed') {
        newMessages.push(`✓ ${f.filename} — completed`);
      } else if (f.status === 'failed') {
        const reason = f.error_message ? `: ${f.error_message}` : '';
        newMessages.push(`✗ ${f.filename} — failed${reason}`);
      } else if (f.status === 'pending') {
        newMessages.push(`— ${f.filename} — never started`);
      }
    });
    const done = (data.completed_files ?? 0);
    const failed = (data.failed_files ?? 0);
    const total = (data.total_files ?? 0);
    newMessages.push(`─── Session finished: ${done}/${total} succeeded, ${failed} failed ───`);
  } else {
    // Live: detect per-file status changes
    const prevFiles = Object.fromEntries(
      (prev.files || []).map(f => [f.filename, f.status])
    );
    (data.files || []).forEach(f => {
      const prevStatus = prevFiles[f.filename];
      if (prevStatus !== f.status) {
        if (f.status === 'downloading') {
          newMessages.push(`↓ ${f.filename} — downloading`);
        } else if (f.status === 'completed') {
          newMessages.push(`✓ ${f.filename} — completed`);
        } else if (f.status === 'failed') {
          const reason = f.error_message ? `: ${f.error_message}` : '';
          newMessages.push(`✗ ${f.filename} — failed${reason}`);
        }
      }
    });
  }

  this.provisioningData = {
    ...prev,
    session_id:        data.session_id        || prev.session_id,
    status:            data.status            ?? prev.status,
    total_files:       data.total_files       ?? prev.total_files,
    completed_files:   data.completed_files   ?? prev.completed_files,
    failed_files:      data.failed_files      ?? prev.failed_files,
    downloading_files: isTerminal ? 0 : (data.downloading_files || 0),
    message:           newMessages,
    files: (data.files || []).map(f => ({
      filename:      f.filename,
      status:        f.status,
      error_message: f.error_message || null,
    })),
  };

  if (isTerminal) {
    this.filesExpanded = true;
    this.logExpanded = true;
    if (!this.provisionDone) {
      this.provisionDone = true;
      this.stopPolling();
    }
  }
},
    async cancelProvisioning() {
      this.cancelling = true;
      try {
        await this.$http.post(`/tenant/${this.entity}/provision/${this.deviceId}/cancel`);
      } catch (e) {
        console.error('Failed to cancel provisioning:', e);
      } finally {
        this.cancelling = false;
      }
    },
    async skipFile(filename) {
      this.skippingFile = filename;
      try {
        await this.$http.post(`/tenant/${this.entity}/provision/${this.deviceId}/skip-file`, { filename });
      } catch (e) {
        console.error('Failed to skip file:', e);
      } finally {
        this.skippingFile = null;
      }
    },
    closeModal() { this.$emit("close-modal"); },
  },
};
</script>

<style scoped>
.stat-label {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.stat-value {
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1.2;
}
.cursor-pointer { cursor: pointer; }

@media (max-width: 576px) {
  .card-body {
    padding: 0.4rem 0.5rem !important;
  }
  .stat-value {
    font-size: 0.95rem;
  }
  h2 {
    font-size: 1.1rem;
  }
}
</style>